import React, { useEffect, useState } from 'react';
import { Button, Form, FormControl, Modal, Stack } from 'react-bootstrap';
import { useError } from "../../contexts/ErrorContext";
import { useSuccess } from "../../contexts/SuccessContext";
import { CalibrationVideosAPI, CamerasAPI } from '../../services/api';
import { CalibrationVideo, Camera } from '../../types';
import { getCameraCalibrationWorkflowSchema } from '../../utils/cameraCalibration';
import UploadModal from '../UploadModal';
import RenderSchemaForm, { SchemaFormData } from '../workflow/RenderSchemaForm';
import CalibrationVideoDisplay from './CalibrationVideoDisplay';
import CameraCard from './CameraCard';

const CameraList: React.FC = () => {
    const [cameras, setCameras] = useState<Camera[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [name, setName] = useState('');
    const [unique_id, setUniqueId] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false)
    const [selectedCalibrationVideo, setSelectedCalibrationVideo] = useState<CalibrationVideo | null>(null);
    const [showCalibrationVideoDetailsModal, setShowCalibrationVideoDetailsModal] = useState(false);
    const [showDeleteCameraConfirm, setShowDeleteCameraConfirm] = useState(false);
    const [cameraToDelete, setCameraToDelete] = useState<Camera | null>(null);
    const [isDeletingCamera, setIsDeletingCamera] = useState(false);
    const { setError } = useError();
    const { setSuccess } = useSuccess();
    const [formData, setFormData] = useState<SchemaFormData>({});
    const [checkValidation, setCheckValidation] = useState(false)
    const [calibratorType, setCalibratorType] = useState<'basic' | 'advanced'>('advanced');


    useEffect(() => {
        fetchCameras();
    }, []);

    useEffect(() => {
        if (!selectedCalibrationVideo) return;
        const foundCamera = cameras.find(camera => camera.calibration_videos.find(v => v.id == selectedCalibrationVideo.id));
        if (!foundCamera) {
            setError('Camera not found');
            return;
        }
        setSelectedCamera(foundCamera)
    }, [selectedCalibrationVideo, cameras, setError]);

    const fetchCameras = async () => {
        try {
            const response = await CamerasAPI.getCameras();
            setCameras(response.data);
        } catch (error) {
            console.error('Failed to fetch cameras', error);
        }
    };

    const showCalibrationVideoDetails = (video: CalibrationVideo) => {
        setShowCalibrationVideoDetailsModal(true);
        setSelectedCalibrationVideo(video);
    };

    const hideCalibrationVideoDetails = () => {
        setShowCalibrationVideoDetailsModal(false);
    };

    const showCreateModal = () => {
        setCreateModalVisible(true);
    };

    const hideCreateModal = () => {
        setCreateModalVisible(false);
        setName('');
        setUniqueId('');
    };

    const hideWorkflowModal = () => {
        setShowFormModal(false)
        setCheckValidation(false)
    };

    const onCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const values = {
                name: name,
                unique_id: unique_id,
            };
            await CamerasAPI.createCamera(values);
            fetchCameras();
            hideCreateModal();
        } catch (error) {
            console.error('Failed to create camera', error);
        }
    };

    const handleUploadComplete = (result?: { success: boolean; message: string }) => {
        if (result) {
            // Show success or error message
            if (result.success) {
                setSuccess(result.message);
                // Refresh cameras to show the new calibration video
                fetchCameras();
            } else {
                setError(result.message);
            }
        }
        // Close the upload modal
        setShowUploadModal(false);
    };

    const handleAddCalibrationVideo = () => {
        setShowUploadModal(true);
    };

    const handleSetCalibrationVideo = async (camera: Camera, videoId: number) => {
        try {
            // If this video is already active, unset it
            const isCurrentlyActive = camera.used_calibration_video?.id === videoId;

            if (isCurrentlyActive) {
                await CamerasAPI.setCalibrationVideo(camera.unique_id, null);
                setSuccess(`Unset active calibration video for camera ${camera.name}`);
            } else {
                await CamerasAPI.setCalibrationVideo(camera.unique_id, videoId);
                setSuccess(`Set calibration video as active for camera ${camera.name}`);
            }

            fetchCameras(); // Refresh the camera list to show the updated calibration video
        } catch (error) {
            console.error('Failed to set calibration video', error);
            setError('Failed to set/unset calibration video');
        }
    };



    const handleStartCalibrationWorkflow = async (videoId: number) => {
        try {
            // Add calibrator_type to the form data before submitting
            const dataWithCalibratorType = {
                ...formData,
                calibrator_type: calibratorType
            };
            await CalibrationVideosAPI.startCalibrationOrchestrator(videoId, dataWithCalibratorType);
            setSuccess('Calibration orchestrator started successfully');
        } catch (error) {
            console.error('Failed to start calibration orchestrator', error);
            setError('Failed to start calibration orchestrator');
        }
    };

    const onValidated = () => {
        setShowFormModal(false)
        setCheckValidation(false)
        if (!selectedCalibrationVideo) {
            return
        }
        handleStartCalibrationWorkflow(selectedCalibrationVideo?.id)
    }

    const handleDeleteCamera = async () => {
        if (!cameraToDelete) return;

        setIsDeletingCamera(true);
        try {
            await CamerasAPI.deleteCamera(cameraToDelete.unique_id);
            setSuccess(`Successfully deleted camera ${cameraToDelete.name} and all its calibration videos`);
            setShowDeleteCameraConfirm(false);
            setCameraToDelete(null);
            fetchCameras();
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || 'Failed to delete camera';
            setError(errorMessage);
        } finally {
            setIsDeletingCamera(false);
        }
    };

    return (
        <div>
            <Stack direction="horizontal" gap={3} className="mb-3">
                <Button variant="primary" onClick={showCreateModal}>
                    Create New Camera
                </Button>
                <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleAddCalibrationVideo}
                >
                    Add Calibration Video
                </Button>
            </Stack>

            <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {cameras.map((camera) => (
                    <CameraCard
                        key={camera.unique_id}
                        camera={camera}
                        onDelete={(camera) => {
                            setCameraToDelete(camera);
                            setShowDeleteCameraConfirm(true);
                        }}
                        onSetCalibrationVideo={handleSetCalibrationVideo}
                        onStartCalibration={(video) => {
                            setSelectedCalibrationVideo(video);
                            setShowFormModal(true);
                        }}
                        onViewDetails={showCalibrationVideoDetails}
                    />
                ))}
            </div>

            {showCalibrationVideoDetailsModal && selectedCalibrationVideo && selectedCamera && (
                <CalibrationVideoDisplay
                    calibrationVideo={selectedCalibrationVideo}
                    isActiveCalibration={
                        selectedCamera.used_calibration_video?.id === selectedCalibrationVideo.id
                    }
                    hide={hideCalibrationVideoDetails}
                    onDelete={() => {
                        fetchCameras();
                        hideCalibrationVideoDetails();
                    }}
                />
            )}

            <Modal show={createModalVisible} onHide={hideCreateModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Create New Camera</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={onCreate}>
                        <Form.Group>
                            <Form.Label>Name</Form.Label>
                            <FormControl
                                type="text"
                                placeholder="Enter name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Unique ID</Form.Label>
                            <FormControl
                                type="text"
                                placeholder="Enter unique ID"
                                value={unique_id}
                                onChange={(e) => setUniqueId(e.target.value)}
                            />
                        </Form.Group>
                        <Button variant="primary" type="submit" className="mt-3">
                            Create
                        </Button>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={hideCreateModal}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            <UploadModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onComplete={handleUploadComplete}
                recordId={undefined}
                recordType={undefined}
            />

            <Modal show={showFormModal} onHide={hideWorkflowModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Start New Calibration {selectedCalibrationVideo?.title} for {selectedCamera?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Calibrator Type</Form.Label>
                        <Form.Select
                            value={calibratorType}
                            onChange={(e) => {
                                const newType = e.target.value as 'basic' | 'advanced';
                                setCalibratorType(newType);
                                // Reset form data when calibrator type changes to apply new defaults
                                setFormData({});
                            }}
                        >
                            <option value="advanced">Advanced (11x9, 75mm squares)</option>
                            <option value="basic">Basic (8x6, 25mm squares)</option>
                        </Form.Select>
                    </Form.Group>

                    <RenderSchemaForm
                        jsonSchema={getCameraCalibrationWorkflowSchema(calibratorType)}
                        formData={formData}
                        setFormData={setFormData}
                        checkValidation={checkValidation}
                        onValidated={onValidated}
                    />

                    <Modal.Footer>
                        <Button variant="primary" onClick={() => setCheckValidation(true)}>
                            Start
                        </Button>
                        <Button variant="secondary" onClick={() => hideWorkflowModal()}>
                            Cancel
                        </Button>
                    </Modal.Footer>

                </Modal.Body>
            </Modal>

            {/* Delete Camera Confirmation Modal */}
            <Modal show={showDeleteCameraConfirm} onHide={() => setShowDeleteCameraConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Camera Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this camera?</p>
                    {cameraToDelete && (
                        <>
                            <p><strong>{cameraToDelete.name}</strong> ({cameraToDelete.unique_id})</p>
                            {cameraToDelete.calibration_videos && cameraToDelete.calibration_videos.length > 0 && (
                                <div className="alert alert-warning">
                                    <strong>Warning:</strong> This will also delete {cameraToDelete.calibration_videos.length} calibration video(s) and their associated blob storage files.
                                </div>
                            )}
                        </>
                    )}
                    <p className="text-muted">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteCameraConfirm(false)} disabled={isDeletingCamera}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteCamera} disabled={isDeletingCamera}>
                        {isDeletingCamera ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Deleting...
                            </>
                        ) : (
                            'Delete Camera'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default CameraList;
